import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MoneyInput } from "@/components/common/MoneyInput";
import { api, apiError } from "@/lib/api";
import { fdate, mxn } from "@/lib/format";
import { MEDIOS_DEVOLUCION, hoyMX } from "@/lib/factoraje";
import { toast } from "sonner";

const MIN_MOTIVO = 10;
const norm = (s) => String(s || "").replace(/\s+/g, "");

// Build 3 · Registrar la devolución del remanente (Mesa de Control / administrador).
export function DevolucionRemanenteDialog({ fila, onClose, onDone, onAcuse }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const hoy = hoyMX();
  useEffect(() => {
    if (fila) setForm({ monto: String(fila.pendiente), fecha: hoy, medio: "spei", cuenta: fila.cuenta_registrada || "", referencia: "", motivo: "" });
  }, [fila]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const distinta = !!fila && norm(form.cuenta) !== norm(fila.cuenta_registrada);
  const monto = Number(form.monto || 0);
  const error = !fila ? "" : monto <= 0 ? "Captura el monto a devolver."
    : monto > Number(fila.pendiente) + 0.005 ? `El monto no puede ser mayor al remanente pendiente (${mxn(fila.pendiente)}).`
    : !norm(form.cuenta) ? "Captura la cuenta destino."
    : distinta && form.motivo.trim().length < MIN_MOTIVO ? `Escribe el motivo del cambio de cuenta (mínimo ${MIN_MOTIVO} caracteres).` : "";

  const guardar = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/factoraje/remanentes/${fila.credito_id}/devoluciones`, { ...form, monto });
      toast.success(`Devolución ${data.folio} registrada: ${mxn(data.monto)}. Remanente pendiente ${mxn(data.remanente?.pendiente)}.`,
        { action: { label: "Descargar acuse", onClick: () => onAcuse(data.id) }, duration: 10000 });
      if (data.cuenta_distinta) toast.warning("Se generó una alerta PLD por devolución a cuenta distinta de la registrada.");
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!fila} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="devolucion-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Registrar devolución del remanente — {fila?.numero_credito}</DialogTitle>
          <DialogDescription>{fila?.cliente} · remanente pendiente <b>{mxn(fila?.pendiente)}</b> · cobro del {fdate(fila?.fecha_cobro)}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="mb-1 block text-xs">Monto a devolver</Label>
            <MoneyInput data-testid="devolucion-monto" value={form.monto} onChange={(v) => set("monto", v)} /></div>
          <div><Label className="mb-1 block text-xs">Fecha de la devolución</Label>
            <Input type="date" data-testid="devolucion-fecha" value={form.fecha || ""} min={fila?.fecha_cobro || undefined} max={hoy}
              onChange={(e) => set("fecha", e.target.value)} /></div>
          <div><Label className="mb-1 block text-xs">Medio</Label>
            <Select value={form.medio} onValueChange={(v) => set("medio", v)}>
              <SelectTrigger data-testid="devolucion-medio"><SelectValue /></SelectTrigger>
              <SelectContent>{MEDIOS_DEVOLUCION.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="mb-1 block text-xs">Referencia</Label>
            <Input data-testid="devolucion-referencia" value={form.referencia || ""} onChange={(e) => set("referencia", e.target.value)} placeholder="Folio SPEI / cheque" /></div>
          <div className="col-span-2"><Label className="mb-1 block text-xs">Cuenta destino</Label>
            <Input data-testid="devolucion-cuenta" value={form.cuenta || ""} onChange={(e) => set("cuenta", e.target.value)} />
            <p className="mt-1 text-[11px] text-slate-500">Registrada en el desembolso: {fila?.cuenta_registrada || "—"}</p></div>
          {distinta && (
            <div className="col-span-2 rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="devolucion-cuenta-distinta">
              <p className="mb-1.5 flex items-start gap-2 text-xs font-semibold text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> La cuenta es distinta a la registrada: se generará una alerta PLD.
              </p>
              <Label className="mb-1 block text-xs text-amber-900">Motivo <span className="text-red-600">*</span></Label>
              <Textarea data-testid="devolucion-motivo" rows={2} value={form.motivo} onChange={(e) => set("motivo", e.target.value)} />
            </div>
          )}
          {error && <p className="col-span-2 text-xs text-rose-700" data-testid="devolucion-error">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button data-testid="devolucion-confirmar" onClick={guardar} disabled={saving || !!error}>
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />} Registrar devolución
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
